/**
 * Analytics Manager
 *
 * Handles analytics service initialization and integration:
 * - Segment (analytics events)
 * - AppsFlyer (attribution tracking)
 *
 * This manager provides:
 * - Analytics initialization (Segment + AppsFlyer)
 * - Attribution data processing
 * - Campaign tracking user updates
 * - Analytics reset on logout
 */

import {
  onInstallConversionData,
  updateUser as updateAppsFlyerUser,
} from 'services/analytics/appsflyer/appsflyer';
import {
  logEvent,
  reset as resetSegment,
} from 'services/analytics/segment/segment';
import { isAndroid, isIOS } from 'shared/constants/platform';
import { logBreadcrumb, logError } from 'shared/utils/sentry/sentry';

// Module state
let attributionData: Record<string, unknown> = {};

/**
 * Initialize analytics (Segment + AppsFlyer)
 * Segment client is already created on module load via segment.ts
 * This ensures AppsFlyer is properly initialized
 */
export function initializeAnalytics() {
  try {
    // Initialize AppsFlyer conversion data listener for mobile only
    // Note: The AppsFlyer SDK itself is initialized by the Segment AppsflyerPlugin
    // (added in segment.native.ts). We only register our conversion data listener here.
    // Calling appsFlyer.initSdk() manually would cause a second native init,
    // resulting in duplicate "AppsFlyer Conversion Data" events.
    if (isIOS || isAndroid) {
      // Set up conversion data handler with callback
      onInstallConversionData(processAttributionData);

      logBreadcrumb({
        level: 'info',
        message: 'AppsFlyer conversion listener registered',
      });
    }

    logBreadcrumb({
      level: 'info',
      message: 'Analytics initialized successfully',
    });
  } catch (error) {
    logError(error);
    logBreadcrumb({
      level: 'error',
      message: 'Failed to initialize analytics',
      data: { error },
    });
    throw error;
  }
}

/**
 * Process AppsFlyer attribution data when received
 *
 * @param data - Attribution data from AppsFlyer install conversion callback
 */
function processAttributionData(data: Record<string, unknown>): void {
  if (!data || Object.keys(data).length === 0) {
    return;
  }

  attributionData = data;

  // Log to Segment
  void logEvent('AppsFlyer Conversion Data', attributionData);

  logBreadcrumb({
    level: 'info',
    message: 'AppsFlyer attribution data received',
    data: attributionData,
  });
}

/**
 * Update campaign tracking user (called after login)
 *
 * @param userId - User ID to associate with AppsFlyer tracking
 */
export function updateCampaignTrackingUser(userId: string): void {
  if (isIOS || isAndroid) {
    updateAppsFlyerUser(userId);

    logBreadcrumb({
      level: 'info',
      message: 'Campaign tracking user updated',
      data: { userId },
    });
  }
}

/**
 * Get attribution data
 * Used by AnalyticsContext to proxy to consumers
 *
 * Returns `attributionData` directly rather than re-reading a snapshot from
 * the AppsFlyer wrapper: that snapshot is always `{}` (truthy) until the SDK
 * resolves, so an `||` fallback to `attributionData` never kicks in and can
 * leave stale data in place once it has already been refreshed.
 *
 * @returns Attribution data from AppsFlyer or empty object for web
 */
export function getAttributionData(): Record<string, unknown> {
  return attributionData;
}

/**
 * Reset analytics state
 * Resets Segment (calls SDK reset and clears risk session ID)
 * Called during logout
 */
export async function resetAnalytics(): Promise<void> {
  try {
    // Reset Segment
    await resetSegment();

    logBreadcrumb({
      level: 'info',
      message: 'Analytics reset successfully',
    });
  } catch (error) {
    logError(error);
    logBreadcrumb({
      level: 'error',
      message: 'Failed to reset analytics',
      data: { error },
    });
  }
}
